import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped']
const notConvertedStatuses = ['inactive', 'Did Not Join']

const convertedStatusSet = new Set(convertedStatuses.map((value) => String(value).trim().toLowerCase()))
const notConvertedStatusSet = new Set(notConvertedStatuses.map((value) => String(value).trim().toLowerCase()))

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const normalizeText = (value) => String(value || '').trim()

const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const campusTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="campusTypeTooltip">
      <strong>{label}</strong>
      <div className="campusTypeTooltipRow">
        <span>Converted</span>
        <strong>{formatCount(row.converted)}</strong>
      </div>
      <div className="campusTypeTooltipRow">
        <span>Not converted</span>
        <strong>{formatCount(row.notConverted)}</strong>
      </div>
      <div className="campusTypeTooltipRow">
        <span>Total</span>
        <strong>{formatCount(row.total)}</strong>
      </div>
      <div className="campusTypeTooltipRow">
        <span>Conversion rate</span>
        <strong>{formatPercent(row.conversionRate)}</strong>
      </div>
    </div>
  )
}

function CampusType() {
  const db = getFirestore(app)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const snapshot = await getDocs(collection(db, 'users'))
      const typeCounts = {}

      snapshot.forEach((docItem) => {
        const data = docItem.data()
        const campusType = normalizeText(data.classType) || 'Unspecified'
        const status = normalizeStatus(data.userStatus) || 'inactive'

        if (!typeCounts[campusType]) {
          typeCounts[campusType] = {
            campusType,
            converted: 0,
            notConverted: 0
          }
        }

        if (convertedStatusSet.has(status)) {
          typeCounts[campusType].converted += 1
        } else if (notConvertedStatusSet.has(status)) {
          typeCounts[campusType].notConverted += 1
        }
      })

      const formattedRows = Object.values(typeCounts)
        .map((row) => {
          const total = row.converted + row.notConverted
          return {
            ...row,
            total,
            conversionRate: total > 0 ? (row.converted / total) * 100 : 0
          }
        })
        .filter((row) => row.total > 0)
        .sort((first, second) => {
          if (second.total !== first.total) {
            return second.total - first.total
          }

          return first.campusType.localeCompare(second.campusType)
        })

      setRows(formattedRows)
    } catch (fetchError) {
      console.error('Failed to load campus type analysis:', fetchError)
      setRows([])
      setError('We could not load the campus type analysis right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return rows
    }

    return rows.filter((row) => {
      return row.campusType.toLowerCase().includes(search)
    })
  }, [rows, searchTerm])

  const chartData = useMemo(() => filteredRows.slice(0, 8), [filteredRows])

  const stats = useMemo(() => {
    return rows.reduce(
      (accumulator, row) => {
        accumulator.total += row.total
        accumulator.converted += row.converted
        accumulator.notConverted += row.notConverted
        return accumulator
      },
      { total: 0, converted: 0, notConverted: 0 }
    )
  }, [rows])

  const overallConversionRate = stats.total > 0 ? (stats.converted / stats.total) * 100 : 0
  const topCampusTypes = rows.slice(0, 5)
  const topCampus = rows[0] || null
  const hasRows = filteredRows.length > 0
  const emptyMessage = rows.length
    ? 'No campus types match the current search.'
    : 'No campus type records were found yet.'

  return (
    <div className="mainContainer campusTypePage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="campusTypeContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Campus Type Distribution</h4>
              <p className="appsSubtext">
                Compare how student conversion is distributed across campus types and quickly spot the strongest and weakest segments.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total records</span>
                <strong>{formatCount(stats.total)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Campus types</span>
                <strong>{formatCount(rows.length)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Converted</span>
                <strong>{formatCount(stats.converted)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Conversion rate</span>
                <strong>{formatPercent(overallConversionRate)}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard campusTypeFiltersCard">
            <div className="appsSearch input-group campusTypeSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search campus type..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="campusTypeFiltersMeta">
              <span>{formatCount(filteredRows.length)} campus types visible</span>
              <span>{formatCount(chartData.length)} charted in the comparison view</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="campusTypeGrid">
            <div className="appsTableCard campusTypeChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Campus type mix
                </h6>
                <span>Top campus types by volume</span>
              </div>
              <div className="campusTypeChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading campus type analysis...</span>
                    </Spinner>
                    <p>Loading campus type analysis...</p>
                  </div>
                ) : hasRows ? (
                  <div className="campusTypeChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          tickFormatter={formatCount}
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="campusType"
                          width={130}
                          tick={{ fontSize: 12, fill: '#475569' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                        />
                        <Tooltip content={campusTooltip} />
                        <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                        <Bar dataKey="converted" name="Converted" stackId="campus" fill="#0f766e" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="notConverted" name="Not converted" stackId="campus" fill="#f59e0b" radius={[0, 10, 10, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>{emptyMessage}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsInsightsCard campusTypeInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Top campus types
                </h6>
                <span>{formatCount(topCampusTypes.length)} shown</span>
              </div>

              <div className="appsInsightsList">
                {topCampusTypes.length ? (
                  topCampusTypes.map((row) => (
                    <div className="appsInsightsItem" key={row.campusType}>
                      <div>
                        <strong>{row.campusType}</strong>
                        <span>{formatCount(row.total)} students</span>
                      </div>
                      <small>{formatPercent(row.conversionRate)} converted</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No campus type insights match the current search.</p>
                  </div>
                )}
              </div>

              <div className="campusTypeMiniStats">
                <div className="campusTypeMiniStat">
                  <span>Highest volume</span>
                  <strong>{topCampus ? topCampus.campusType : '—'}</strong>
                  <small>{topCampus ? formatCount(topCampus.total) : 'No data'}</small>
                </div>
                <div className="campusTypeMiniStat">
                  <span>Converted share</span>
                  <strong>{formatPercent(overallConversionRate)}</strong>
                  <small>{formatCount(stats.notConverted)} not converted</small>
                </div>
              </div>

              <p className="appsInsightsMore">
                Search a campus type to narrow the view, or refresh after new student records sync.
              </p>
            </div>
          </div>

          <div className="appsTableCard campusTypeTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Campus summary
              </h6>
              <span>{formatCount(filteredRows.length)} results</span>
            </div>
            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading campus summary...</span>
                  </Spinner>
                  <p>Loading campus summary...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable campusTypeTable">
                  <thead>
                    <tr>
                      <th>Campus Type</th>
                      <th>Converted</th>
                      <th>Not Converted</th>
                      <th>Total</th>
                      <th>Conversion Rate</th>
                      <th>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      filteredRows.map((row) => {
                        const progress = topCampus?.total > 0 ? (row.total / topCampus.total) * 100 : 0

                        return (
                          <tr key={row.campusType}>
                            <td>
                              <div className="campusTypeRowLabel">
                                <strong>{row.campusType}</strong>
                                <span>{row.total > 0 ? 'Tracked type' : 'No records'}</span>
                              </div>
                            </td>
                            <td>{formatCount(row.converted)}</td>
                            <td>{formatCount(row.notConverted)}</td>
                            <td>
                              <span className="campusTypeTotalCell">{formatCount(row.total)}</span>
                            </td>
                            <td>{formatPercent(row.conversionRate)}</td>
                            <td>
                              <div className="campusTypeTrack" aria-hidden="true">
                                <span className="campusTypeTrackFill" style={{ width: `${Math.min(progress, 100)}%` }} />
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CampusType
